import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, Loader2, Save } from "lucide-react";
import { DashboardWidgetTile } from "../DashboardWidgetTile";
import { WidgetGrid } from "../WidgetGrid";
import { useCreateDashboard } from "../hooks";
import {
  resolveRowValueExpression,
  sortWidgetsByPosition,
  type Dashboard,
  type DashboardDefinition,
  type DashboardParameter,
  type DashboardParameterValue,
  type DashboardWidget,
  type WidgetDataRow,
  type WidgetInteractionPointContext,
} from "../types";
import {
  expandParams,
  initializeParameterValues,
} from "../views/parameters";
import styles from "./DashboardArtifactPanel.module.css";

/**
 * Right-side artifact panel for the home chat surface.
 *
 * Renders the in-flight dashboard preview as the agent streams widgets
 * via `propose_widget` / `propose_dashboard`. Combines the canonical
 * proposal (committed via propose_dashboard) with the in-flight
 * streaming overlay so the panel fills in tile-by-tile as the agent
 * drafts each widget.
 *
 * Differences from `PreviewColumn` (used by the dedicated dashboard
 * create/edit chat):
 *   - No resize handle / collapse toggle — sized by the parent layout.
 *   - No "Apply" callback chain — the panel POSTs to /dashboards
 *     directly via `useCreateDashboard` so it works in the home chat
 *     without the dashboards-create plumbing.
 *   - Skeleton placeholder when neither a proposal nor any streamed
 *     widgets are present yet (the agent might be running probes
 *     before its first propose_widget call).
 */
export interface DashboardArtifactPanelProps {
  /**
   * Canonical proposal from the agent's most recent `propose_dashboard`
   * commit. Null while the agent is still drafting (only streamed
   * widgets have arrived) or before the first proposal.
   */
  proposal: {
    title: string;
    description?: string | null;
    definition: DashboardDefinition;
    explanation: string;
    is_public?: boolean;
  } | null;
  /**
   * In-flight widgets streamed via `propose_widget`. Map keyed by
   * position. Combined with `proposal.definition.widgets` so streamed
   * tiles override same-position committed widgets and new positions
   * append to the preview.
   */
  streamingWidgets: Map<number, DashboardWidget>;
  onSaved?: (dashboard: Dashboard) => void;
  onDismiss?: () => void;
}

export function DashboardArtifactPanel({
  proposal,
  streamingWidgets,
  onSaved,
  onDismiss,
}: DashboardArtifactPanelProps) {
  const createDashboard = useCreateDashboard();
  const {
    mutate: createDashboardMutate,
    isPending: isCreateDashboardPending,
    error: createDashboardError,
  } = createDashboard;

  // Effective preview definition: streaming widgets overlay onto the
  // canonical proposal at their declared positions. New positions
  // append; same-position tiles get replaced.
  const effective = useMemo(() => {
    const baseDefinition = proposal?.definition ?? { widgets: [] };
    const merged = new Map<number, DashboardWidget>();
    for (const widget of baseDefinition.widgets ?? []) {
      merged.set(widget.layout.position, widget);
    }
    for (const [position, widget] of streamingWidgets) {
      merged.set(position, {
        ...widget,
        layout: { ...widget.layout, position },
      });
    }
    const widgets = sortWidgetsByPosition(
      Array.from(merged.values()).map((w) => w),
    );
    return {
      title: proposal?.title ?? "Drafting dashboard…",
      description: proposal?.description ?? null,
      explanation:
        proposal?.explanation ??
        (streamingWidgets.size > 0
          ? `Streaming ${streamingWidgets.size} widget${streamingWidgets.size === 1 ? "" : "s"}…`
          : "The agent is exploring your data — widgets will stream in here as they're drafted."),
      parameters: (baseDefinition.parameters ?? []) as DashboardParameter[],
      widgets,
      isCommitted: proposal !== null,
    };
  }, [proposal, streamingWidgets]);

  // Same parameter handling as the dedicated preview — keep the panel
  // interactive (clicking a row fires drilldowns, etc.) so the chat
  // artifact behaves like the saved view.
  const parametersSig = useMemo(() => JSON.stringify(effective.parameters), [effective.parameters]);
  const [parameterValues, setParameterValues] = useState<
    Record<string, DashboardParameterValue>
  >(() => initializeParameterValues(effective.parameters));
  useEffect(() => {
    setParameterValues((current) => {
      const seeded = initializeParameterValues(effective.parameters);
      for (const key of Object.keys(seeded)) {
        if (
          Object.prototype.hasOwnProperty.call(current, key) &&
          current[key] !== null &&
          current[key] !== undefined
        ) {
          seeded[key] = current[key];
        }
      }
      return seeded;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parametersSig]);

  const valuesSig = useMemo(() => JSON.stringify(parameterValues), [parameterValues]);
  const resolvedParams = useMemo(
    () => expandParams(parameterValues, new Date()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [valuesSig],
  );

  const handleInteraction = useCallback(
    (widget: DashboardWidget, row: WidgetDataRow, point?: WidgetInteractionPointContext) => {
      const event = point ? "point_click" : "row_click";
      const interaction = widget.interactions?.find((i) => i.event === event);
      if (!interaction) return;
      const sets = interaction.set_parameters ?? {};
      if (Object.keys(sets).length === 0) return;
      setParameterValues((current) => {
        const allMatch = Object.entries(sets).every(([paramId, expression]) => {
          const incoming = resolveRowValueExpression(expression, row, point);
          const existing = current[paramId];
          return (
            existing !== null &&
            existing !== undefined &&
            String(existing) === String(incoming)
          );
        });
        const next = { ...current };
        for (const [paramId, expression] of Object.entries(sets)) {
          next[paramId] = allMatch ? null : resolveRowValueExpression(expression, row, point);
        }
        return next;
      });
    },
    [],
  );

  const onSave = useCallback(() => {
    if (!proposal) return;
    createDashboardMutate(
      {
        title: proposal.title || "Untitled dashboard",
        description: proposal.description ?? undefined,
        is_public: proposal.is_public ?? false,
        definition: proposal.definition,
      },
      {
        onSuccess: (saved) => {
          onSaved?.(saved);
        },
      },
    );
  }, [createDashboardMutate, proposal, onSaved]);

  const isEmpty = effective.widgets.length === 0;

  return (
    <div className={styles.panel}>
      <header className={styles.header}>
        <div className={styles.headerCopy}>
          <p className={styles.eyebrow}>Dashboard preview</p>
          <h2 className={styles.title} title={effective.title}>
            {effective.title}
          </h2>
          {effective.description ? (
            <p className={styles.description}>{effective.description}</p>
          ) : null}
          {effective.explanation ? (
            <p className={styles.explanation}>{effective.explanation}</p>
          ) : null}
        </div>
        <div className={styles.headerActions}>
          {onDismiss ? (
            <button
              type="button"
              className={styles.dismissButton}
              onClick={onDismiss}
              title="Hide artifact"
            >
              Dismiss
            </button>
          ) : null}
          <button
            type="button"
            className={styles.saveButton}
            onClick={onSave}
            disabled={!proposal || isCreateDashboardPending}
          >
            {isCreateDashboardPending ? (
              <Loader2 size={14} className={styles.spinner} aria-hidden />
            ) : (
              <Save size={14} aria-hidden />
            )}
            {isCreateDashboardPending ? "Saving…" : "Save dashboard"}
          </button>
        </div>
      </header>
      {createDashboardError ? (
        <div className={styles.errorBanner} role="alert">
          <AlertCircle size={16} aria-hidden />
          <span>{createDashboardError.message}</span>
        </div>
      ) : null}
      <div className={styles.body}>
        {isEmpty ? (
          <div className={styles.emptyState}>
            <p className={styles.emptyTitle}>Drafting…</p>
            <p className={styles.emptyBody}>
              The agent is exploring your data. Widgets will stream into this
              panel as soon as the first one is ready.
            </p>
          </div>
        ) : (
          <WidgetGrid widgets={effective.widgets} resolvedParams={resolvedParams}>
            {(widget) => (
              <DashboardWidgetTile
                widget={widget}
                params={resolvedParams}
                visibilityPrechecked
                onInteraction={handleInteraction}
              />
            )}
          </WidgetGrid>
        )}
      </div>
    </div>
  );
}
